import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { updateProject, getNextStepTemplates } from '../services/api';
import { PROJECT_STATUS_ORDER, projectStatusLabel } from '../lib/workStatus';

export const ProjectStatusDialog = ({ open, onOpenChange, project, onUpdated, t }) => {
  const [status, setStatus] = useState(project?.status || 'not_started');
  const [comment, setComment] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [templates, setTemplates] = useState({});
  const [autoFilled, setAutoFilled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStatus(project?.status === 'active' ? 'in_progress' : project?.status || 'not_started');
    setComment('');
    setNextStep(project?.next_step || '');
    setAutoFilled(false);
    getNextStepTemplates().then((data) => setTemplates(data.templates || {})).catch(() => setTemplates({}));
  }, [open, project]);

  const changeStatus = (value) => {
    setStatus(value);
    const template = (templates[value] || '').trim();
    const currentIsTemplate = autoFilled || !nextStep.trim() || Object.values(templates).some((tpl) => (tpl || '').trim() === nextStep.trim());
    if (value !== project?.status && template && currentIsTemplate) {
      setNextStep(template);
      setAutoFilled(true);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateProject(project.id, { status, status_comment: comment || null, next_step: nextStep });
      toast.success(`Statuss nomainīts: ${projectStatusLabel(t, status)}`);
      onOpenChange(false);
      onUpdated?.(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās nomainīt statusu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="project-status-dialog">
        <DialogHeader>
          <DialogTitle>Mainīt objekta statusu</DialogTitle>
          <DialogDescription>Klients saņems paziņojumu par statusa maiņu. Komentārs būs redzams statusu vēsturē.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Jaunais statuss</Label>
            <Select value={status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="project-status-dialog-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...PROJECT_STATUS_ORDER, 'on_hold'].map((value) => (
                  <SelectItem key={value} value={value} data-testid={`project-status-dialog-option-${value}`}>{projectStatusLabel(t, value)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="flex items-center justify-between">
              <span>Kas notiks tālāk? (redz klients)</span>
              {autoFilled && <span className="flex items-center gap-1 text-[11px] font-normal text-[#1F3D2B]" data-testid="project-status-dialog-autofilled"><Sparkles className="w-3 h-3" /> aizpildīts no veidnes</span>}
            </Label>
            <Textarea rows={3} value={nextStep} onChange={(e) => { setNextStep(e.target.value); setAutoFilled(false); }} placeholder="Piem.: gaidām VMD pārbaudi, tad sagatavosim dokumentus" data-testid="project-status-dialog-next-step" />
          </div>
          <div className="space-y-1">
            <Label>Komentārs vēsturei (neobligāti)</Label>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Piem.: darbi dabā pabeigti" data-testid="project-status-dialog-comment" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="project-status-dialog-cancel">Atcelt</Button>
            <Button onClick={save} disabled={saving || (status === project?.status && nextStep === (project?.next_step || ''))} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="project-status-dialog-save">
              {saving ? 'Saglabā...' : 'Saglabāt'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectStatusDialog;
